import { Injectable } from '@angular/core';
import {
    ChapterType,
    IQuotationCost,
    IQuotationsQuotation,
    IQuotationTraveller,
    IQuotationVatConfiguration
} from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { ITableHeaders, ITableRow, TableRowType } from "app_quotation/shared/services/table-data/ITable";
import { feesChapterHeader, servicesChapterHeader, TableDataService, tripChapterHeader } from "app_quotation/shared/services/table-data/table-data.service";
import { Yii } from "libraries/interfaces/Yii";
import { CalculationMode } from "libraries/interfaces/IWenji";
import { VatService } from "app_quotation/shared/services/vat.service";
import { QuotationBlockCategoryCodeEnum } from "libraries/interfaces/quotation-block-category-code.enum";
import {IRetailCalculationConfig} from "../../retail-grid/interfaces/ICalculationConfig";
import {IRetailCalculationCategoryValue} from "../../retail-grid/interfaces/ICalculationCategoryValue";
import { EQuotationEntryPoints } from "libraries/enums/e-quotation-entry-points";

@Injectable()
export class RetailTableDataService {

    constructor(
        private readonly tableDataService: TableDataService,
        private readonly vatService: VatService,
    ) {
    }

    public computeTableData(quotation: IQuotationsQuotation, subPage: EQuotationEntryPoints): ITableRow[] {
        let total: IQuotationCost;
        if (this.tableDataService.hasCost(quotation)) {
            total = {
                amount: quotation.cost.amount,
                currency: quotation.cost.currency,
            };
        }

        const valueHeaders: ITableRow[] = [
            {
                title: Yii.t('wui-quotation-js', "Prix de vente total inclus dans cette grille"),
                total,
                totalRetail: this.tableDataService.hasRetail(quotation) ? quotation.retail : null,
                totalVat: this.tableDataService.hasVat(quotation) ? quotation.vat : null,
                totalRetailIncludingVat: this.tableDataService.hasRetailAndVat(quotation) ? quotation.retailIncludingVat : null,
                type: TableRowType.totalHeader,
                travellers: extractValueHeaderTravellers(quotation),
            },
            {
                title: Yii.t('wui-quotation-js', "Ajustements"),
                type: TableRowType.adjustmentHeader,
                totalRetail: this.tableDataService.hasRetail(quotation) ? quotation.retail : null,
                totalVat: this.tableDataService.hasVat(quotation) ? quotation.vat : null,
                totalRetailIncludingVat: this.tableDataService.hasRetailIncludingVat(quotation) ? quotation.retailIncludingVat : null,
                travellers: extractValueHeaderTravellers(quotation),
            },
        ];

        switch (subPage) {
            case EQuotationEntryPoints.option:
                return [
                    tripChapterHeader,
                    ...extractOnlyBlocksWithCost(this.tableDataService.extractBlocksFromSection(quotation, ChapterType.trip, subPage)),
                    servicesChapterHeader,
                    ...extractOnlyBlocksWithCost(this.tableDataService.extractBlocksFromSection(quotation, ChapterType.services, subPage)),
                ];
            case EQuotationEntryPoints.included:
            default:
                return [
                    ...valueHeaders,
                    tripChapterHeader,
                    ...extractOnlyBlocksWithCost(this.tableDataService.extractBlocksFromSection(quotation, ChapterType.trip, subPage)),
                    feesChapterHeader,
                    ...extractOnlyBlocksWithCost(this.tableDataService.extractBlocksFromSection(quotation, ChapterType.fees, subPage)),
                    servicesChapterHeader,
                    ...extractOnlyBlocksWithCost(this.tableDataService.extractBlocksFromSection(quotation, ChapterType.services, subPage)),
                ];
        }

    }

    public computeTableHeaders(
        quotation: IQuotationsQuotation, calculationMode: CalculationMode, subPages: EQuotationEntryPoints, vatConfiguration: IQuotationVatConfiguration,
        showCommissionHeaders: boolean = false
    ): ITableHeaders {
        let margin: string;
        switch (calculationMode) {
            case CalculationMode.markup:
                margin = Yii.t("wui-quotation-js", "Markup");
                break;
            case CalculationMode.margin:
            default:
                margin = Yii.t("wui-quotation-js", "Marge");
                break;
        }
        let title: string;
        switch (subPages) {
            case EQuotationEntryPoints.option:
                title = Yii.t("wui-quotation-js", "Prix de vente en option");
                break;
            case EQuotationEntryPoints.included:
            default:
                title = Yii.t("wui-quotation-js", "Prix de vente inclus");
                break;
        }

        const tableHeaders: ITableHeaders = {
            title,
            total: Yii.t("wui-quotation-js", "Total Achat"),
            margin,
            gain: this.vatService.getValueAccordingToVat(
                quotation,
                Yii.t("wui-quotation-js", "Gains"),
                Yii.t("wui-quotation-js", "Gains HT")),
        };
        if(showCommissionHeaders){
            tableHeaders.commission = Yii.t("wui-quotation-js", "Taux Com.");
            tableHeaders.commissionTotal = Yii.t("wui-quotation-js", "Mont. Com.");
        }
        if (vatConfiguration.vatRate) {
            tableHeaders.vat = Yii.t("wui-quotation-js", "TVA");
        }
        tableHeaders.retailTotal = this.vatService.getValueAccordingToVat(
            quotation,
            Yii.t("wui-quotation-js", "Total Vente"),
            Yii.t("wui-quotation-js", "Total Vente HT"),
            Yii.t("wui-quotation-js", "Total Vente TTC"));
        tableHeaders.travellers = this.tableDataService.extractHeaderTravellers(quotation);

        return tableHeaders;
    }

    public extractCategoryMarginMarkupCommission(
        tableRows: ITableRow[],
        retailCalculation: IRetailCalculationConfig,
        calculationMode: CalculationMode
    ): Map<QuotationBlockCategoryCodeEnum, number> {

        const map: Map<QuotationBlockCategoryCodeEnum, number> = new Map();

        const usedCategories: QuotationBlockCategoryCodeEnum[] = tableRows
            .filter((tableRow: ITableRow) => tableRow.block)
            .map((tableRow: ITableRow) => tableRow.block.category)
            .reduce(
                (codes: QuotationBlockCategoryCodeEnum[], code: QuotationBlockCategoryCodeEnum): QuotationBlockCategoryCodeEnum[] => {
                    if (codes.indexOf(code) === -1) {
                        return [...codes, code];
                    }
                    return codes;
                }, []);

        usedCategories.forEach((categoryCode: QuotationBlockCategoryCodeEnum) => {
            const values: number[] = tableRows
                .filter((tableRow: ITableRow) => tableRow.block && tableRow.block.category.toLowerCase() === categoryCode.toLowerCase())
                .map((tableRow: ITableRow) => {
                    if (tableRow.block.retail &&
                        tableRow.block.retail[calculationMode] &&
                        tableRow.block.retail[calculationMode].used) {
                        return tableRow.block.retail[calculationMode].used;
                    }
                    return getCalculationValue(retailCalculation, categoryCode, calculationMode);
                })
                .reduce(
                    (valueList: number[], value: number): number[] => {
                        if (valueList.indexOf(value) === -1) {
                            return [...valueList, value];
                        }
                        return valueList;
                    }, []);

            map.set(categoryCode, values.length === 1 ? values[0] : getCalculationValue(retailCalculation, categoryCode, calculationMode));
        });

        return map;
    }

}

function getCalculationValue(
    retailCalculation: IRetailCalculationConfig,
    categoryCode: QuotationBlockCategoryCodeEnum,
    calculationMode: CalculationMode
): number {

    const categoryConfig: IRetailCalculationCategoryValue = retailCalculation.category
        .find((category: IRetailCalculationCategoryValue) => category.code.toLowerCase() === categoryCode.toLowerCase());

    return categoryConfig ? categoryConfig[calculationMode] : retailCalculation[calculationMode];

}

function extractValueHeaderTravellers(quotation: IQuotationsQuotation): IQuotationTraveller[] {
    return (quotation.travellers || [])
        .map((traveller: IQuotationTraveller) => {
            return {
                id: traveller.id,
                retail: traveller.retail,
                vat: traveller.vat,
                retailIncludingVat: traveller.retailIncludingVat,
            };
        })
        .sort((a: IQuotationTraveller, b: IQuotationTraveller) => a.id - b.id);
}

function extractOnlyBlocksWithCost(rows: ITableRow[]): ITableRow[] {
    return rows.filter((row: ITableRow) => row.block.cost && (row.block.cost.amount || row.block.cost.amount === 0));
}
